import { Check, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

type RoleOption = { id: string; name: string }

type Props = {
  roles: RoleOption[]
  value: string[]
  onChange: (ids: string[]) => void
  className?: string
  listClassName?: string
}

export function RoleChecklist({ roles, value, onChange, className, listClassName }: Props) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const selected = new Set(value)

  const list = useMemo(() => {
    const term = q.trim().toLocaleLowerCase('tr')
    if (!term) return roles
    return roles.filter((r) => r.name.toLocaleLowerCase('tr').includes(term))
  }, [roles, q])

  const toggle = (id: string) => onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id])

  if (!roles.length) {
    return <p className="text-sm text-muted-foreground">{t('common.empty')}</p>
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('access.directory.form.roleSearch')}
          aria-label={t('access.directory.form.roleSearch')}
          className="pl-8"
        />
      </div>
      {value.length ? (
        <p className="text-xs text-muted-foreground">{t('access.directory.form.roleSelected', { count: value.length })}</p>
      ) : null}
      <ul className={cn('max-h-48 divide-y overflow-y-auto scrollbar-none rounded-xl border', listClassName)}>
        {list.map((r) => {
          const on = selected.has(r.id)
          return (
            <li key={r.id}>
              <button
                type="button"
                aria-pressed={on}
                onClick={() => toggle(r.id)}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-muted/60',
                  on && 'bg-brand-blue/[0.05]',
                )}
              >
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.name}</span>
                <span
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded-md border',
                    on ? 'border-brand-blue bg-brand-blue text-white' : 'border-input',
                  )}
                >
                  {on ? <Check className="size-3.5" /> : null}
                </span>
              </button>
            </li>
          )
        })}
        {list.length === 0 ? <li className="p-4 text-center text-sm text-muted-foreground">{t('common.empty')}</li> : null}
      </ul>
    </div>
  )
}
